import { useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { ColorName } from 'types';

import { ordersApi } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { setOrderId, setOrderNumber } from 'redux/slices/settings';

import { ordersDataService } from 'services';

import { getApiErrorMessage } from 'helpers/getApiErrorMessage';

import { OrderAddressPicker, pickedAddresses } from 'components/OrderAddressBlocks';

import * as S from './CreateOrderForm.styles';

export type CreateOrderFormProps = {
  /** Delegated mode: receives the form values (`customer`, `shippingAddressCode`, …). */
  onSubmit?: (data: FieldValues) => void;
  onSuccess?: () => void;
  /** Shows a Cancel button next to Create. */
  onCancel?: () => void;
  isLoading?: boolean;
};

export function CreateOrderForm(props: CreateOrderFormProps): React.ReactElement {
  const { onSubmit: onSubmitProp, onSuccess, onCancel, isLoading = false } = props;

  const dispatch = useAppDispatch();
  const { control, handleSubmit, setValue } = useForm();
  const { t } = useTranslation();
  const { zone, season, warehouse, marketLine, barColor } = useAppSelector(
    (state) => state.settings
  );

  const [isSubmitLoading, isSubmitLoadingSet] = useState<boolean>(false);

  const handleFormSubmit = async (data: FieldValues) => {
    if (onSubmitProp) {
      onSubmitProp(data);
    } else {
      isSubmitLoadingSet(true);
      try {
        const res = await ordersDataService.post(
          season,
          zone,
          warehouse.code,
          data.customer,
          marketLine,
          pickedAddresses(data)
        );
        const { orderId, orderNumber } = res.data;
        dispatch(setOrderId(orderId));
        dispatch(setOrderNumber(String(orderNumber)));
        dispatch(ordersApi.util.invalidateTags(['DropdownOrders', 'OrdersList']));
        localStorage.setItem('orderNumber', String(orderNumber));
        localStorage.setItem('orderId', orderId);
        if (onSuccess) {
          onSuccess();
        }
      } catch (e: unknown) {
        // A refused order (the customer was blocked meanwhile, another zone, ...) says why, instead of nothing.
        toast.error(getApiErrorMessage(e, t('order.failedGeneric')));
      } finally {
        isSubmitLoadingSet(false);
      }
    }
  };

  const submitDisabled = onSubmitProp ? isLoading : isSubmitLoading;
  const submitLoading = onSubmitProp ? isLoading : isSubmitLoading;

  return (
    <>
      <S.Title scale="xl3" barColor={barColor as ColorName}>
        {t('order.startNew')}
      </S.Title>
      <S.Description>{t('form.lookupPlaceholder')}</S.Description>
      <S.Container>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <OrderAddressPicker
            control={control}
            setValue={setValue}
            zone={zone}
            color={(barColor as ColorName) || undefined}
          />

          <S.Actions>
            {onCancel ? (
              <S.Cancel
                type="button"
                motive="actionSecondary"
                $color={(barColor as ColorName) || undefined}
                onClick={onCancel}
              >
                {t('common.cancel')}
              </S.Cancel>
            ) : null}
            <S.Submit
              disabled={submitDisabled}
              isLoading={submitLoading}
              motive="actionPrimary"
              barColor={barColor as ColorName}
            >
              {t('common.create')}
            </S.Submit>
          </S.Actions>
        </form>
      </S.Container>
    </>
  );
}
